import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '@/store/tenantStore';
import { getJobDetails } from '@/services/api/homeApi';
import { ShareJobMenu } from '@/features/jobs/components/ShareJobMenu';
import { EasyApplyButton } from '@/features/jobs/components/EasyApplyButton';
import {
  buildJobShareUrl,
  formatExperience,
  formatString,
} from '@/features/jobs/utils/jobHelpers';
import type { JobEntity } from '@/types/job.types';
import { jobMetaLine } from './HomeJobListItem';
import styles from './HomeJobDetailPanel.module.css';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { OnboardingJobButton } from '@/features/onboarding/entry';

/**
 * HomeJobDetailPanel — right-hand preview of the job selected in the Home list.
 * Search results don't always carry the description, so the full requisition is
 * fetched (and cached) per selected job; list data renders immediately meanwhile.
 * Sections use Flutter JobDetailsView's labels: Job Information, Required Skills /
 * Other Skills (split on isMandatory). The title opens the job page (Flutter onTapTitle).
 */

interface HomeJobDetailPanelProps {
  entity: JobEntity;
  onSave: () => void;
  onUnsave: () => void;
  onClickApply: (isEasyApply: boolean) => void;
  onViewDetails: () => void;
  /** Replaces the default Apply buttons (My Jobs shows the application status actions). */
  actions?: ReactNode;
}

export function HomeJobDetailPanel({
  entity,
  onSave,
  onUnsave,
  onClickApply,
  onViewDetails,
  actions,
}: HomeJobDetailPanelProps) {
  const { t } = useTranslation();
  const config = useTenantStore((s) => s.config);
  const enableEasyApply = !!(config as Record<string, unknown> | null)?.enableEasyApply;

  const displayId = entity.displayId ?? '';
  const { data: details, isLoading } = useQuery({
    queryKey: ['jobDetails', displayId],
    queryFn: () => getJobDetails(displayId),
    enabled: !!displayId,
    staleTime: 5 * 60_000,
  });

  const description = details?.jobDescription || entity.jobDescription || '';
  const skills = details?.skills?.length ? details.skills : entity.skills ?? [];
  // Flutter JobDetailsViewBloc: isMandatory → requiredSkills, everything else → otherSkills
  const requiredSkills = skills.filter((s) => s.isMandatory).map((s) => s.skill ?? '');
  const otherSkills = skills.filter((s) => !s.isMandatory).map((s) => s.skill ?? '');
  const employment = formatString(
    details?.employmentType || entity.employmentType || details?.jobType || entity.jobType,
  );
  const hasExperience =
    entity.requiredExperienceInMonths?.from != null || entity.requiredExperienceInMonths?.to != null;

  const isApplied = !!(entity.isApplied || entity.isEasyApplied);

  return (
    <aside className={styles.panel} aria-label={t('Job details')}>
      <div className={styles.head}>
        <div className={styles.headRow}>
          <div className={styles.headText}>
            <h2 className={styles.title}>
              <button type="button" className={styles.titleLink} onClick={onViewDetails}>
                {entity.jobTitle ?? 'N/A'}
              </button>
            </h2>
            <p className={styles.meta}>{jobMetaLine(entity)}</p>
          </div>
          <div className={styles.headActions}>
            {/* Full job page in a new tab — same action as the title (onViewDetails) */}
            <button
              type="button"
              className={styles.iconBtn}
              onClick={onViewDetails}
              aria-label={t('Open in new tab')}
              title={t('Open in new tab')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              </svg>
            </button>
            <ShareJobMenu
              shareUrl={buildJobShareUrl(displayId)}
              jobId={entity.id ?? ''}
              jobTitle={entity.jobTitle ?? 'N/A'}
            />
            <button
              type="button"
              className={`${styles.iconBtn} ${entity.isSavedJob ? styles.saved : ''}`}
              onClick={entity.isSavedJob ? onUnsave : onSave}
              disabled={entity.isLoadingForSaveJobButton}
              aria-label={entity.isSavedJob ? t('Unsave job') : t('Save job')}
              title={entity.isSavedJob ? t('Unsave job') : t('Save job')}
            >
              {entity.isLoadingForSaveJobButton ? (
                <span className={styles.spinner} />
              ) : (
                <svg width="16" height="16" viewBox="0 0 20 20" fill={entity.isSavedJob ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
                  <path d="M5 3h10a1 1 0 0 1 1 1v13.5l-6-3-6 3V4a1 1 0 0 1 1-1Z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className={styles.applyRow}>
          {actions ?? (isApplied ? (
            <>
              <span className={styles.appliedBadge}>{t('Applied')}</span>
              {/* Candidate onboarding (React only, FLUTTER_DEVIATIONS §5): only on a job the candidate is hired for, by
                  the page's HiredRequisitionsProvider; nothing in prod */}
              <OnboardingJobButton
                requisitionId={entity.id}
                requisitionDisplayId={entity.displayId}
                className={styles.onboardingBtn}
                label={t('Onboarding In Progress')}
              />
            </>
          ) : (
            <>
              {enableEasyApply && (
                <EasyApplyButton className={styles.easyApplyBtn} onClick={() => onClickApply(true)} />
              )}
              <button type="button" className={styles.applyBtn} onClick={() => onClickApply(false)}>
                {t('Apply')}
              </button>
            </>
          ))}
        </div>
      </div>

      <div className={styles.body}>
        {/* Flutter hides "Job Information" when the description is empty */}
        {(description || isLoading) && (
          <section className={styles.block}>
            <h3 className={styles.blockTitle}>{t('Job Information')}</h3>
            {description ? (
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
              />
            ) : (
              <div className={styles.shimmerLines}>
                <span />
                <span />
                <span />
              </div>
            )}
          </section>
        )}

        <SkillBlock title={t('Required Skills')} skills={requiredSkills} isRequired />
        <SkillBlock title={t('Other Skills')} skills={otherSkills} />

        <dl className={styles.facts}>
          {hasExperience && (
            <div>
              <dt>{t('Experience')}</dt>
              <dd>
                {formatExperience(
                  entity.requiredExperienceInMonths?.from,
                  entity.requiredExperienceInMonths?.to,
                )}
              </dd>
            </div>
          )}
          {employment && (
            <div>
              <dt>{t('Employee status')}</dt>
              {/* As on the job page (Flutter JobDetailsView): translateString(formatString(...)) */}
              <dd>{t(employment)}</dd>
            </div>
          )}
          <div>
            <dt>{t('Job ID')}</dt>
            <dd>{displayId || 'N/A'}</dd>
          </div>
        </dl>
      </div>
    </aside>
  );
}

function SkillBlock({ title, skills, isRequired = false }: { title: string; skills: string[]; isRequired?: boolean }) {
  if (skills.length === 0) return null;
  return (
    <section className={styles.block}>
      <h3 className={styles.blockTitle}>{title}</h3>
      <ul className={styles.skillList}>
        {skills.map((skill, i) => (
          <li key={`${skill}-${i}`} className={styles.skillRow}>
            <span className={`${styles.dot} ${isRequired ? styles.dotRequired : ''}`} aria-hidden="true" />
            <span className={styles.skillName}>{skill}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
